import * as PopoverPrimitive from '@radix-ui/react-popover';
import {
  Bookmark,
  BookmarkPlus,
  Check,
  ChevronDown,
  Pin,
  PinOff,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useLocation, useNavigate } from 'react-router-dom';
import { applyViewToUrl, useSavedViews } from '@/state/savedViews';
import { filtersToQuery, useFilters } from '@/state/filters';
import { notify } from '@/lib/toast';
import { cn } from '@/lib/cn';

/**
 * Saved Views button + popover.
 *
 *   Trigger: shows the count of saved views (or "Save view" if empty).
 *   List:    pinned views first, then most-recently updated, with quick
 *            actions (apply, refresh, pin, rename, delete).
 *   Footer:  inline "Save current as…" form for the current filter combo.
 *
 * Animation notes (Emil): the popover is origin-aware via `pop-anim`;
 * row hover is color-only; the inline form slides in via a CSS transition,
 * not a keyframe, so re-opening feels snappy.
 */
export function SavedViewsButton() {
  const navigate = useNavigate();
  const location = useLocation();

  const views = useSavedViews(useShallow((s) => s.views));
  const saveCurrent = useSavedViews((s) => s.saveCurrent);
  const updateView = useSavedViews((s) => s.update);
  const refreshView = useSavedViews((s) => s.refresh);
  const removeView = useSavedViews((s) => s.remove);

  const range = useFilters((s) => s.range);
  const storeIds = useFilters((s) => s.storeIds);
  const compareMode = useFilters((s) => s.compareMode);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const storeKey = storeIds.join(',');

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [name, setName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  // The query the active row would match — used to highlight "this is what's loaded".
  const currentQuery = useMemo(
    () => filtersToQuery({ range, storeIds, compareMode, timeRangeMode }),
    // storeKey is the stable signature; storeIds reference changes on every set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [range, storeKey, compareMode, timeRangeMode],
  );

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setName('');
    setRenamingId(null);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  const sorted = useMemo(() => {
    const list = [...views];
    list.sort((a, b) => {
      if (!!b.pinned !== !!a.pinned) return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
      return b.updated_at.localeCompare(a.updated_at);
    });
    return list;
  }, [views]);
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return sorted;
    return sorted.filter((v) => v.name.toLowerCase().includes(needle));
  }, [sorted, query]);

  function commitApply(id: string) {
    const view = views.find((v) => v.id === id);
    if (!view) return;
    applyViewToUrl(view, navigate, location.pathname);
    setOpen(false);
    notify.success(`Loaded "${view.name}"`, { duration: 1800 });
  }

  function commitSave() {
    const trimmed = name.trim();
    if (!trimmed) {
      notify.warning('Give your view a name first.');
      nameRef.current?.focus();
      return;
    }
    const v = saveCurrent(trimmed);
    setName('');
    notify.success(`Saved "${v.name}"`, { duration: 2000 });
  }

  function commitRename(id: string, next: string) {
    const trimmed = next.trim();
    if (!trimmed) return;
    updateView(id, { name: trimmed });
    setRenamingId(null);
  }

  function togglePin(id: string, pinned: boolean) {
    updateView(id, { pinned });
  }

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          className="press inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-surface px-2.5 text-xs focus-ring"
        >
          <Bookmark className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          <span className="hidden md:inline">Views</span>
          <span className="kpi-num text-muted-foreground">{views.length || ''}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden />
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          className={cn(
            'pop-anim z-[1000] w-[320px] overflow-hidden rounded-md border border-border bg-surface-elevated text-foreground shadow-soft',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'duration-150 ease-out',
          )}
        >
          <div className="flex items-center gap-2 border-b border-border px-2 py-1.5">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search views…"
              className="h-7 w-full bg-transparent text-xs outline-none placeholder:text-muted-foreground"
            />
            {query ? (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            ) : null}
          </div>

          <div className="max-h-[280px] overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                {views.length === 0 ? (
                  <>
                    No saved views yet.
                    <br />
                    Save your current filters below to jump back here in one click.
                  </>
                ) : (
                  <>
                    Nothing matches <span className="font-medium text-foreground">"{query}"</span>.
                  </>
                )}
              </div>
            ) : (
              filtered.map((v) => {
                const isActive = v.query === currentQuery;
                const renaming = renamingId === v.id;
                return (
                  <div
                    key={v.id}
                    className={cn(
                      'group flex items-center gap-2 rounded-sm px-2 py-1.5 text-xs transition-colors duration-100 ease-out hover:bg-muted',
                      isActive && 'bg-muted/60',
                    )}
                  >
                    <Check
                      className={cn(
                        'h-3.5 w-3.5 shrink-0',
                        isActive ? 'text-primary opacity-100' : 'opacity-0',
                      )}
                      aria-hidden
                    />
                    {renaming ? (
                      <input
                        autoFocus
                        defaultValue={v.name}
                        onBlur={(e) => commitRename(v.id, e.currentTarget.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitRename(v.id, e.currentTarget.value);
                          if (e.key === 'Escape') setRenamingId(null);
                        }}
                        className="h-6 flex-1 rounded border border-input bg-surface px-1.5 text-xs outline-none focus-ring"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => commitApply(v.id)}
                        onDoubleClick={() => setRenamingId(v.id)}
                        className="min-w-0 flex-1 truncate text-left"
                        title="Click to apply · double-click to rename"
                      >
                        {v.name}
                      </button>
                    )}
                    <span className="hidden text-[10px] text-muted-foreground/70 sm:inline">
                      {prettyAge(v.updated_at)}
                    </span>
                    <div className="flex items-center gap-0.5 opacity-0 transition-opacity duration-100 ease-out group-hover:opacity-100">
                      <RowAction
                        label={v.pinned ? 'Unpin' : 'Pin'}
                        onClick={() => togglePin(v.id, !v.pinned)}
                      >
                        {v.pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
                      </RowAction>
                      <RowAction label="Refresh with current filters" onClick={() => refreshView(v.id)}>
                        <RefreshCw className="h-3 w-3" />
                      </RowAction>
                      <RowAction
                        label="Delete"
                        tone="danger"
                        onClick={() => {
                          removeView(v.id);
                          notify.message(`Removed "${v.name}"`, { duration: 1500 });
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </RowAction>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              commitSave();
            }}
            className="flex items-center gap-2 border-t border-border bg-muted/30 px-2 py-1.5"
          >
            <BookmarkPlus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <input
              ref={nameRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Save current as…"
              className="h-7 w-full bg-transparent text-xs outline-none placeholder:text-muted-foreground"
            />
            <button
              type="submit"
              disabled={!name.trim()}
              className="press inline-flex items-center gap-1 rounded border border-border bg-surface px-2 py-0.5 text-[11px] font-medium hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 focus-ring"
            >
              Save
            </button>
          </form>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

function RowAction({
  children,
  label,
  onClick,
  tone,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
  tone?: 'danger';
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        'press rounded p-1 text-muted-foreground hover:bg-surface focus-ring',
        tone === 'danger' && 'hover:text-danger',
      )}
      aria-label={label}
      title={label}
      tabIndex={-1}
    >
      {children}
    </button>
  );
}

function prettyAge(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return `${Math.floor(d / 7)}w`;
}
